import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Search,
  Loader2,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  FlaskConical,
  Trash2,
  Download,
  Check,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  useCrmUsersPaged,
  useCrmUserSearch,
  useLabVisitsByUser,
  useLabVisitCostBreakdown,
  useCompanies,
  useDeleteLabVisit,
  useOverrideLabVisitStatus,
  useCrmSubscriptionTiers,
  useExportCrmCustomers,
} from "@/hooks/api";
import type { CrmUser, LabVisit, LabVisitUpdate } from "@/lib/api_client";
import { useAuth } from "@/lib/auth";
import { visitFlowLabel } from "@/lib/utils/b2bFlowLabel";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MaskedText } from "@/components/masked-text";
import { CreateLabVisitButton } from "@/components/create-lab-visit-button";
import {
  formatCurrency,
  formatDate,
  timeAgo,
  getTierVariant,
  translateStatus,
} from "./formatters";
import { CustomerFiltersPanel, CustomerColumnInfo } from "./customer-filters";
import { emptyCustomerFilters, customerCsv, genderLabel, subscriptionLabel, sortCustomers, validateCustomerFilters, type CustomerSortKey as SortKey, type CustomerSortDir as SortDir } from "./customer-table-data";
import { downloadCsv } from "@/lib/utils/csv";

// ── Sort helpers ──────────────────────────────────────────────

// ── User Table ───────────────────────────────────────────────

const ROW_HEIGHT_COLLAPSED = 48;
const ROW_HEIGHT_EXPANDED = 600;

const COL_GROUP = (
  <colgroup>
    <col style={{ width: "16%" }} />
    <col style={{ width: "18%" }} />
    <col style={{ width: "15%" }} />
    <col style={{ width: "8%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "10%" }} />
    <col style={{ width: "5%" }} />
    <col style={{ width: "8%" }} />
    <col style={{ width: "6%" }} />
    <col style={{ width: "5%" }} />
  </colgroup>
);

export function UserTable({ priceSource }: { priceSource: string }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => () => clearTimeout(searchTimer.current), []);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(value), 300);
  };
  const [sortKey, setSortKey] = useState<SortKey>("fullName");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filters, setFilters] = useState({ ...emptyCustomerFilters });
  const filterError = validateCustomerFilters(filters);
  const { data: tiers, isError: tiersError } = useCrmSubscriptionTiers();
  const exportCustomers = useExportCrmCustomers();
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // The roster is paged and sorted by the server; a search is answered by the
  // server as a whole and only sorted here.
  const searchActive = debouncedSearch.trim().length >= 2;
  const order = `${sortKey}.${sortDir},authUserId.asc`;
  const paged = useCrmUsersPaged(order, filters, !searchActive && !filterError);
  const searchQuery = useCrmUserSearch(debouncedSearch, filters, !filterError);
  const { data: searchHits, isFetching: searching } = searchQuery;
  const queryError = searchActive ? searchQuery.isError : paged.isError;

  const rows = useMemo(
    () =>
      searchActive
        ? sortCustomers(searchHits ?? [], sortKey, sortDir)
        : (paged.data?.pages.flat() ?? []),
    [searchActive, searchHits, sortKey, sortDir, paged.data],
  );
  const loading = searchActive ? searching && !searchHits : paged.isLoading;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [order, filters, debouncedSearch]);

  const estimateSize = useCallback(
    (index: number) =>
      expandedUserId != null && rows[index]?.id === expandedUserId
        ? ROW_HEIGHT_EXPANDED
        : ROW_HEIGHT_COLLAPSED,
    [rows, expandedUserId],
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    overscan: 10,
  });

  // Ask for the next page once the viewport nears the end of what is loaded.
  const virtualItems = virtualizer.getVirtualItems();
  const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index ?? -1;
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = paged;
  useEffect(() => {
    if (searchActive || filterError || paged.isError || !hasNextPage || isFetchingNextPage) return;
    if (lastVisibleIndex >= rows.length - 20) void fetchNextPage();
  }, [
    lastVisibleIndex,
    rows.length,
    searchActive,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    filterError,
    paged.isError,
  ]);

  const handleExport = async () => {
    const exportedAt = new Date().toISOString();
    try {
      const users = await exportCustomers.mutateAsync({ filters, search: debouncedSearch, sortKey, sortDir });
      if (!users.length) { toast.info("Ingen kunder å eksportere med valgte filtre"); return; }
      downloadCsv(customerCsv(users, filters, debouncedSearch, order, exportedAt), `kunder-${exportedAt.slice(0, 10)}.csv`, { bom: true });
      toast.success(`${users.length} kunder eksportert`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Kunne ikke eksportere kunder");
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col)
      return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
    return sortDir === "asc" ? (
      <ChevronUp className="h-3.5 w-3.5" />
    ) : (
      <ChevronDown className="h-3.5 w-3.5" />
    );
  };

  return (
    <>
      <div className="flex items-end justify-between gap-3">
      <div className="max-w-md flex-1">
        <label htmlFor="customer-search" className="text-xs text-admin-text-secondary">Søk etter navn eller e-post</label>
        <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-admin-text-subtle" />
        <Input
          id="customer-search"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Sok pa navn eller e-post..."
          className="pl-9"
        />
        </div>
      </div>
      <Button variant="outline" onClick={handleExport} disabled={exportCustomers.isPending || !!filterError || search !== debouncedSearch}>
        {exportCustomers.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Eksporter CSV
      </Button>
      </div>
      <CustomerFiltersPanel filters={filters} onChange={setFilters} tiers={tiers ?? []} tiersError={tiersError} />
      <p className="text-xs text-admin-text-subtle">CSV inneholder alle treff og personopplysninger, valgte filtre og eksporttidspunkt.</p>
      {filterError && <p role="alert" className="text-sm text-admin-danger-500">{filterError}</p>}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <div className="min-w-[1200px]">
          {/* Sticky header */}
          <table className="w-full text-sm table-fixed">
            {COL_GROUP}
            <thead>
              <tr className="border-b border-admin-card-border">
                <SortableHeader
                  label="Navn"
                  col="fullName"
                  onSort={handleSort}
                  icon={<SortIcon col="fullName" />}
                />
                <SortableHeader
                  label="E-post"
                  col="email"
                  onSort={handleSort}
                  icon={<SortIcon col="email" />}
                />
                <SortableHeader
                  label="Abonnement"
                   col="subscriptionTier"
                  onSort={handleSort}
                   icon={<SortIcon col="subscriptionTier" />}
                   definition="subscription"
                />
                <SortableHeader
                   label="Labbesøk"
                   col="usedVisits"
                  onSort={handleSort}
                   icon={<SortIcon col="usedVisits" />}
                   definition="visits"
                />
                <SortableHeader
                  label="Totalt brukt"
                  col="totalAmountSpent"
                  onSort={handleSort}
                  icon={<SortIcon col="totalAmountSpent" />}
                />
                <SortableHeader
                  label="Opprettet"
                   col="accountCreated"
                  onSort={handleSort}
                   icon={<SortIcon col="accountCreated" />}
                />
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                  Aktiv
                </th>
                <SortableHeader label="Wearables" col="wearablesConnected" onSort={handleSort} icon={<SortIcon col="wearablesConnected" />} definition="wearables" />
                <SortableHeader label="Kjønn" col="gender" onSort={handleSort} icon={<SortIcon col="gender" />} />
                <SortableHeader label="Alder" col="age" onSort={handleSort} icon={<SortIcon col="age" />} />
              </tr>
            </thead>
          </table>

          {/* Virtualized scrollable body */}
          <div
            ref={scrollRef}
            className="overflow-y-auto"
            style={{ height: "max(300px, calc(100vh - 500px))" }}
          >
            {queryError ? (
              <div role="alert" className="p-8 text-admin-danger-500 text-sm">Kunne ikke hente kunder. <Button variant="outline" size="sm" onClick={() => void (searchActive ? searchQuery.refetch() : paged.refetch())}>Prøv igjen</Button></div>
            ) : filterError ? null : loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" />
              </div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-admin-text-subtle text-sm">
                Ingen kunder funnet
              </div>
            ) : (
              <div
                style={{
                  height: virtualizer.getTotalSize(),
                  position: "relative",
                  width: "100%",
                }}
              >
                {virtualItems.map((virtualRow) => {
                  const user = rows[virtualRow.index]!;
                  const isExpanded = expandedUserId === user.id;
                  return (
                    <div
                      key={user.id}
                      data-index={virtualRow.index}
                      ref={virtualizer.measureElement}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <table className="w-full text-sm table-fixed">
                        {COL_GROUP}
                        <tbody>
                          <UserRow
                            user={user}
                            isExpanded={isExpanded}
                            onToggleExpand={() =>
                              setExpandedUserId(isExpanded ? null : user.id)
                            }
                            onNavigateAnamnesis={(authUserId) =>
                              navigate(`/anamnesis/${authUserId}`)
                            }
                            priceSource={priceSource}
                          />
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
            {isFetchingNextPage && (
              <div className="flex items-center justify-center gap-2 py-3 text-xs text-admin-text-subtle">
                <Loader2 className="h-3 w-3 animate-spin" />
                Henter flere kunder...
              </div>
            )}
          </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

// ── Sortable Header ──────────────────────────────────────────

function SortableHeader({
  label,
  col,
  onSort,
  icon,
  className,
  definition,
}: {
  label: string;
  col: SortKey;
  onSort: (key: SortKey) => void;
  icon: React.ReactNode;
  className?: string;
  definition?: "subscription" | "visits" | "wearables";
}) {
  return (
    <th
      className={`text-left text-xs font-medium text-admin-text-subtle p-3 ${className ?? ""}`}
    >
      <div className="flex items-center gap-1">
      <button
        className="flex items-center gap-1 hover:text-admin-text-primary transition-colors cursor-pointer"
        onClick={() => onSort(col)}
      >
        {label}
        {icon}
      </button>
      {definition && <CustomerColumnInfo definition={definition} />}
      </div>
    </th>
  );
}

// ── User Row ─────────────────────────────────────────────────

function UserRow({
  user,
  isExpanded,
  onToggleExpand,
  onNavigateAnamnesis,
  priceSource,
}: {
  user: CrmUser;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onNavigateAnamnesis: (authUserId: string) => void;
  priceSource: string;
}) {
  const fullName =
    user.fullName ??
    ([user.givenName, user.familyName].filter(Boolean).join(" ") || "Ukjent");
  const isActive = !!user.hasActiveSubscription;

  return (
    <>
      <tr
        className="border-b border-admin-card-border last:border-0 hover:bg-admin-card/50 cursor-pointer transition-colors"
        onClick={onToggleExpand}
      >
        <td className="p-3">
          <span className="font-medium text-admin-text-primary">
            <MaskedText value={fullName} />
          </span>
        </td>
        <td className="p-3 text-admin-text-secondary">
          {user.email ? <MaskedText value={user.email} type="email" /> : "-"}
        </td>
        <td className="p-3">
          <Badge variant={getTierVariant(user.subscriptionTier)} className="whitespace-normal break-words">
            {subscriptionLabel(user.subscriptionTier)}
          </Badge>
        </td>
        <td className="p-3 text-admin-text-secondary">
          {user.usedVisits ?? 0}
        </td>
        <td className="p-3 text-admin-text-secondary text-right tabular-nums">
          {formatCurrency(parseFloat(user.totalAmountSpent ?? "0") / 100)}
        </td>
        <td className="p-3 text-admin-text-secondary">
          {formatDate(user.accountCreated)}
        </td>
        <td className="p-3">
          <span
            role="img"
            aria-label={isActive ? "Aktivt abonnement" : "Ikke aktivt abonnement"}
            className={`inline-block h-2 w-2 rounded-full ${isActive ? "bg-admin-success-500" : "bg-admin-text-subtle/40"}`}
          />
        </td>
        <td className="p-3 text-admin-text-secondary">{user.wearablesConnected ? "Ja" : "Nei"}</td>
        <td className="p-3 text-admin-text-secondary">{genderLabel(user.gender)}</td>
        <td className="p-3 text-admin-text-secondary">{user.age ?? "Ukjent"}</td>
      </tr>
      {isExpanded && (
        <tr className="bg-admin-bg/50 border-b border-admin-card-border">
          <td colSpan={10} className="p-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <DetailItem
                label="Fodselsdato"
                value={formatDate(user.birthDate)}
              />
              <DetailItem
                label="Kjonn"
                value={
                  user.gender === "male"
                    ? "Mann"
                    : user.gender === "female"
                      ? "Kvinne"
                      : (user.gender ?? "-")
                }
              />
              <DetailItem
                label="Lab"
                value={
                  user.labName
                    ? `${user.labName}${user.labCity ? `, ${user.labCity}` : ""}`
                    : "-"
                }
              />
              <DetailItem
                label="Profil opprettet"
                value={formatDate(user.profileCreated)}
              />
              <DetailItem
                label="Neste trekk"
                value={formatDate(user.nextChargeDate)}
              />
              <DetailItem
                label="Sporreskjema"
                value={
                  user.healthSurveyCompleted ? "Fullfort" : "Ikke fullfort"
                }
              />
              <DetailItem
                label="Sist oppdatert"
                value={timeAgo(user.latestUpdate)}
              />
            </div>
            <LifetimeFinancials user={user} />
            {user.authUserId && (
              <UserLabVisitsTable
                authUserId={user.authUserId}
                priceSource={priceSource}
              />
            )}
            {user.authUserId && (
              <div className="mt-3 pt-3 border-t border-admin-card-border flex items-center gap-4">
                <button
                  className="text-sm text-admin-primary hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigateAnamnesis(user.authUserId!);
                  }}
                >
                  Apne anamnese →
                </button>
                <span onClick={(e) => e.stopPropagation()}>
                  <CreateLabVisitButton
                    userId={user.authUserId}
                    userName={fullName}
                  />
                </span>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── User Lab Visits Table ────────────────────────────────────

const RESULT_STATUSES = new Set([
  "allBiometricResultsReceived",
  "assessmentCompleted",
]);

/** Every value of the backend's lab_visit_status enum, in workflow order. */
const LAB_VISIT_STATUSES: NonNullable<LabVisitUpdate["status"]>[] = [
  "paid",
  "bloodTestRequested",
  "pendingRequisition",
  "requisitioned",
  "awaitingResults",
  "allBiometricResultsReceived",
  "assessmentCompleted",
  "requestRejected",
];

function UserLabVisitsTable({
  authUserId,
  priceSource,
}: {
  authUserId: string;
  priceSource: string;
}) {
  const { data: labVisits, isLoading } = useLabVisitsByUser(authUserId);
  const { data: companies } = useCompanies();
  const companyNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of companies ?? []) map.set(c.id, c.name);
    return map;
  }, [companies]);
  const [costModalVisitId, setCostModalVisitId] = useState<string | null>(null);
  const [visitToDelete, setVisitToDelete] = useState<LabVisit | null>(null);
  const { hasRole } = useAuth();
  // Overrides go through the revert-status route, which writes the row and its
  // status history and deliberately enqueues no jobs — no email, push or Slack.
  // The route requires canReachLabVisit (clinician or BHT staff); the CRM's own
  // roles (crm/superuser) are not clinical, so the control is limited to admins.
  const canOverrideStatus = hasRole("admin");
  const overrideStatus = useOverrideLabVisitStatus();
  // Rule 4 in docs/backoffice-table-system.md: nothing is sent before the
  // admin presses Save. The dropdown only stages a value; the draft is
  // discarded with X or when the expanded row closes.
  const [statusDrafts, setStatusDrafts] = useState<
    Record<string, NonNullable<LabVisitUpdate["status"]>>
  >({});

  const setStatusDraft = (
    visitId: string,
    currentStatus: LabVisit["status"],
    status: NonNullable<LabVisitUpdate["status"]>,
  ) => {
    setStatusDrafts((prev) => {
      const next = { ...prev };
      if (status === currentStatus) delete next[visitId];
      else next[visitId] = status;
      return next;
    });
  };

  const cancelStatusDraft = (visitId: string) => {
    setStatusDrafts((prev) => {
      const next = { ...prev };
      delete next[visitId];
      return next;
    });
  };

  const saveStatusOverride = (visit: LabVisit) => {
    const status = statusDrafts[visit.id];
    if (!status || status === visit.status) return;
    overrideStatus.mutate(
      { id: visit.id, status },
      {
        onSuccess: () => {
          cancelStatusDraft(visit.id);
          toast.success(`Status endret til ${translateStatus(status)}`);
        },
        onError: (e) =>
          toast.error(
            e instanceof Error && e.message
              ? e.message
              : "Kunne ikke endre status",
          ),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="mt-3 pt-3 border-t border-admin-card-border">
        <div className="flex items-center gap-2 text-xs text-admin-text-subtle">
          <Loader2 className="h-3 w-3 animate-spin" />
          Henter labbesok...
        </div>
      </div>
    );
  }

  if (!labVisits?.length) return null;

  return (
    <div
      className="mt-3 pt-3 border-t border-admin-card-border"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-xs font-medium text-admin-text-subtle mb-2">
        Labbesok ({labVisits.length})
      </p>
      <div className="border border-admin-card-border rounded overflow-hidden max-h-48 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-admin-card">
            <tr className="border-b border-admin-card-border">
              <th className="text-left p-2 font-medium text-admin-text-subtle">
                Dato
              </th>
              <th className="text-left p-2 font-medium text-admin-text-subtle">
                ID
              </th>
              <th className="text-left p-2 font-medium text-admin-text-subtle">
                Type
              </th>
              <th className="text-left p-2 font-medium text-admin-text-subtle">
                Bedrift
              </th>
              <th className="text-left p-2 font-medium text-admin-text-subtle">
                Status
              </th>
              <th className="text-left p-2 font-medium text-admin-text-subtle">
                Helfo
              </th>
              <th className="text-left p-2 font-medium text-admin-text-subtle">
                Selvrapportert
              </th>
              <th className="text-right p-2 font-medium text-admin-text-subtle">
                Kostnad
              </th>
              <th className="w-8 p-2" />
            </tr>
          </thead>
          <tbody>
            {labVisits.map((lv) => {
              const hasResults =
                RESULT_STATUSES.has(lv.status) && !lv.selfReported;
              // Unknown only once the lookup has answered, not while loading.
              const companyName = lv.companyId
                ? (companyNameById.get(lv.companyId) ??
                  (companies ? "Ukjent bedrift" : null))
                : null;
              const statusDraft = statusDrafts[lv.id];
              const statusDirty =
                statusDraft !== undefined && statusDraft !== lv.status;
              return (
                <tr
                  key={lv.id}
                  className="border-b border-admin-card-border last:border-0 hover:bg-admin-card/30"
                >
                  <td className="p-2 text-admin-text-secondary tabular-nums">
                    {formatDate(lv.visitDate)}
                  </td>
                  <td
                    className="p-2 text-admin-text-subtle font-mono"
                    title={lv.id}
                  >
                    {lv.id.slice(0, 8)}...
                  </td>
                  <td className="p-2">
                    <Badge
                      variant={lv.companyId ? "secondary" : "outline"}
                      className="text-[10px] px-1.5 py-0 whitespace-nowrap"
                    >
                      {visitFlowLabel(lv)}
                    </Badge>
                  </td>
                  <td className="p-2 text-admin-text-secondary">
                    {companyName ?? (
                      <span className="text-admin-text-subtle">-</span>
                    )}
                  </td>
                  <td className="p-2">
                    {canOverrideStatus ? (
                      <div className="flex items-center gap-1">
                        <Select
                          value={statusDraft ?? lv.status}
                          disabled={overrideStatus.isPending}
                          title="Velg ny status, og trykk Lagre. Overstyringen sender ingen e-post, push eller Slack."
                          aria-label={`Overstyr status for labbesøk ${lv.id.slice(0, 8)}`}
                          onChange={(e) =>
                            setStatusDraft(
                              lv.id,
                              lv.status,
                              e.target
                                .value as NonNullable<LabVisitUpdate["status"]>,
                            )
                          }
                          className="h-6 w-auto max-w-[170px] px-1.5 py-0 text-[10px]"
                        >
                          {LAB_VISIT_STATUSES.map((status) => (
                            <option key={status} value={status}>
                              {translateStatus(status)}
                            </option>
                          ))}
                        </Select>
                        {statusDirty && (
                          <>
                            <button
                              className="cursor-pointer text-admin-success-400 hover:text-admin-success-300 disabled:cursor-not-allowed disabled:opacity-50"
                              title="Lagre status"
                              aria-label="Lagre status"
                              disabled={overrideStatus.isPending}
                              onClick={() => saveStatusOverride(lv)}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button
                              className="cursor-pointer text-admin-text-subtle hover:text-admin-text-primary disabled:cursor-not-allowed disabled:opacity-50"
                              title="Avbryt statusendring"
                              aria-label="Avbryt statusendring"
                              disabled={overrideStatus.isPending}
                              onClick={() => cancelStatusDraft(lv.id)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0"
                      >
                        {translateStatus(lv.status)}
                      </Badge>
                    )}
                  </td>
                  <td className="p-2 text-admin-text-secondary">
                    {lv.helfo ? "Ja" : "Nei"}
                  </td>
                  <td className="p-2 text-admin-text-secondary">
                    {lv.selfReported ? "Ja" : "Nei"}
                  </td>
                  <td className="p-2 text-right">
                    {hasResults ? (
                      <button
                        className="text-admin-primary hover:underline tabular-nums cursor-pointer"
                        onClick={() => setCostModalVisitId(lv.id)}
                      >
                        <FlaskConical className="h-3 w-3 inline mr-0.5" />
                        Se kostnad
                      </button>
                    ) : (
                      <span className="text-admin-text-subtle">-</span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {canDeleteLabVisit(lv.status) && (
                      <button
                        className="text-admin-text-subtle hover:text-admin-negative cursor-pointer"
                        title="Slett labbesøk"
                        aria-label="Slett labbesøk"
                        onClick={() => setVisitToDelete(lv)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {costModalVisitId && (
        <LabVisitCostModal
          labVisitId={costModalVisitId}
          onClose={() => setCostModalVisitId(null)}
          priceSource={priceSource}
        />
      )}

      <DeleteLabVisitDialog
        visit={visitToDelete}
        companyName={
          visitToDelete?.companyId
            ? (companyNameById.get(visitToDelete.companyId) ?? null)
            : null
        }
        onClose={() => setVisitToDelete(null)}
      />
    </div>
  );
}

// ── Delete Lab Visit Dialog ──────────────────────────────────

/** Mirrors DELETABLE_LAB_VISIT_STATUSES in backend lab-visits/routes.ts. */
const DELETABLE_STATUSES = new Set<string>([
  "paid",
  "bloodTestRequested",
  "pendingRequisition",
  "requisitioned",
]);

function canDeleteLabVisit(status: string): boolean {
  return DELETABLE_STATUSES.has(status);
}

function DeleteLabVisitDialog({
  visit,
  companyName,
  onClose,
}: {
  visit: LabVisit | null;
  companyName: string | null;
  onClose: () => void;
}) {
  const deleteLabVisit = useDeleteLabVisit();

  const handleDelete = async () => {
    if (!visit) return;
    try {
      await deleteLabVisit.mutateAsync(visit.id);
      toast.success("Labbesøket ble slettet");
      onClose();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Kunne ikke slette labbesøket",
      );
    }
  };

  return (
    <Dialog
      open={visit !== null}
      onOpenChange={(open) => {
        if (!open && !deleteLabVisit.isPending) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Slett labbesøk</DialogTitle>
        </DialogHeader>
        {visit && (
          <div className="mt-2 space-y-3 text-sm text-admin-text-secondary">
            <div className="space-y-1">
              <p>
                {formatDate(visit.visitDate)} · {visitFlowLabel(visit)}
                {companyName ? ` · ${companyName}` : ""} ·{" "}
                {translateStatus(visit.status)}
              </p>
              <p className="font-mono text-xs text-admin-text-subtle">
                {visit.id}
              </p>
            </div>
            <p>
              Sletter besøket og alt som henger på det — rekvisisjoner,
              skjemasvar og notater. Dette kan ikke angres.
            </p>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={deleteLabVisit.isPending}
          >
            Avbryt
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            disabled={deleteLabVisit.isPending}
          >
            {deleteLabVisit.isPending && (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            )}
            Slett
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Lab Visit Cost Modal ─────────────────────────────────────
// Lists the same rows the CRM period stats charge, so the total here matches
// the dashboard. Anything on the visit but not on our bill — a HELFO-covered
// marker, a stray Furst result on a HELFO-only visit — is listed at 0 kr.

function LabVisitCostModal({
  labVisitId,
  onClose,
  priceSource,
}: {
  labVisitId: string;
  onClose: () => void;
  priceSource: string;
}) {
  const { data, isLoading, isError, error } = useLabVisitCostBreakdown(labVisitId, priceSource);
  const facilityFee = data?.facilityFee;
  const biomarkerTotal = data?.biomarkerTotal;

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Biomarkorer og kostnad</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-admin-text-subtle" />
          </div>
        ) : isError ? (
          <p className="text-sm text-admin-danger-500 py-4">
            Feil ved henting av kostnad:{" "}
            {error instanceof Error ? error.message : "Ukjent feil"}
          </p>
        ) : !data || data.items.length === 0 ? (
          <p className="text-sm text-admin-text-subtle py-4">
            Ingen biomarkorer funnet for dette besoket.
          </p>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-admin-text-subtle">
              Labbesok:{" "}
              <span className="font-mono">{labVisitId.slice(0, 8)}...</span>{" "}
              &middot; {data.items.length} biomarkorer (deduplisert etter
              furstId)
            </p>
            <div className="border border-admin-card-border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-admin-card-border bg-admin-card">
                    <th className="text-left p-2.5 font-medium text-admin-text-subtle text-xs">
                      Biomarkor
                    </th>
                    <th className="text-left p-2.5 font-medium text-admin-text-subtle text-xs">
                      Furst ID
                    </th>
                    <th className="text-right p-2.5 font-medium text-admin-text-subtle text-xs">
                      Verdi
                    </th>
                    <th className="text-left p-2.5 font-medium text-admin-text-subtle text-xs">
                      Kilde
                    </th>
                    <th className="text-right p-2.5 font-medium text-admin-text-subtle text-xs">
                      Pris
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr
                      key={item.markerKey}
                      className="border-b border-admin-card-border last:border-0"
                    >
                      <td className="p-2.5 text-admin-text-primary">
                        {item.biometricName}
                      </td>
                      <td className="p-2.5 text-admin-text-subtle font-mono text-xs">
                        {item.furstId ?? "-"}
                      </td>
                      <td className="p-2.5 text-admin-text-secondary text-right tabular-nums">
                        {item.value ? `${item.value} ${item.unit ?? ""}`.trim() : "-"}
                      </td>
                      <td className="p-2.5 text-admin-text-subtle text-xs">
                        {item.helfo
                          ? "HELFO"
                          : !item.charged
                            ? "Ikke fakturert"
                            : (item.priceSource ?? "-")}
                      </td>
                      <td className="p-2.5 text-right tabular-nums font-medium">
                        {!item.charged
                          ? "0 kr"
                          : item.price
                            ? `${parseFloat(item.price).toLocaleString("nb-NO")} kr`
                            : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {biomarkerTotal && facilityFee && parseFloat(facilityFee) > 0 ? (
                  <tfoot>
                    <tr className="border-t border-admin-card-border">
                      <td colSpan={4} className="p-2.5 text-xs text-admin-text-subtle">
                        Biomarkorer subtotal
                      </td>
                      <td className="p-2.5 text-right tabular-nums font-medium text-admin-text-secondary">
                        {parseFloat(biomarkerTotal).toLocaleString("nb-NO")} kr
                      </td>
                    </tr>
                    <tr className="border-t border-admin-card-border">
                      <td colSpan={4} className="p-2.5 text-xs text-admin-text-subtle">
                        Prøvetakingsgebyr
                      </td>
                      <td className="p-2.5 text-right tabular-nums font-medium text-admin-text-secondary">
                        {parseFloat(facilityFee).toLocaleString("nb-NO")} kr
                      </td>
                    </tr>
                    <tr className="bg-admin-card border-t border-admin-card-border">
                      <td colSpan={4} className="p-2.5 text-sm font-medium text-admin-text-primary">
                        Totalt
                      </td>
                      <td className="p-2.5 text-right tabular-nums font-bold text-admin-text-primary">
                        {parseFloat(data.total).toLocaleString("nb-NO")} kr
                      </td>
                    </tr>
                  </tfoot>
                ) : (
                  <tfoot>
                    <tr className="bg-admin-card border-t border-admin-card-border">
                      <td colSpan={4} className="p-2.5 text-sm font-medium text-admin-text-primary">
                        Totalt
                      </td>
                      <td className="p-2.5 text-right tabular-nums font-bold text-admin-text-primary">
                        {parseFloat(data.total).toLocaleString("nb-NO")} kr
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Lifetime Financials ──────────────────────────────────────

function LifetimeFinancials({ user }: { user: CrmUser }) {
  const grossIncome = (parseFloat(user.totalAmountSpent ?? "0") || 0) / 100;
  const refunded = (parseFloat(user.totalRefunded ?? "0") || 0) / 100;
  const netIncome = grossIncome - refunded;
  const labCost = parseFloat(user.totalLabCost ?? "0") || 0;
  const profit = netIncome - labCost;

  return (
    <div className="mt-3 pt-3 border-t border-admin-card-border">
      <p className="text-xs font-medium text-admin-text-subtle mb-2">
        Livstidsokonomi
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <DetailItem
          label="Brutto inntekt"
          value={formatCurrency(grossIncome)}
        />
        <DetailItem
          label="Refundert"
          value={refunded > 0 ? `-${formatCurrency(refunded)}` : "0 kr"}
        />
        <DetailItem label="Netto inntekt" value={formatCurrency(netIncome)} />
        <DetailItem
          label="Labkostnad (Furst avtale)"
          value={formatCurrency(labCost)}
        />
        <div>
          <p className="text-xs text-admin-text-subtle">Resultat</p>
          <p
            className={`text-sm font-medium ${profit >= 0 ? "text-admin-success-600" : "text-admin-danger-500"}`}
          >
            {formatCurrency(profit)}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Detail Item ──────────────────────────────────────────────

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-admin-text-subtle">{label}</p>
      <p className="text-sm text-admin-text-primary font-medium">{value}</p>
    </div>
  );
}
